import type { CSSProperties } from 'react';

import { toMachineNumber } from '@shared/format';
import { cn } from '@shared/ui/cn';

/**
 * The share a row holds of its section's whole.
 *
 * **Not a chart.** The design system puts charts out of this version and names
 * no chart shape; this is the documented progress bar at `--radius-pill`, filled
 * from a `--progress` custom property — the one dynamic value the design system
 * permits inside markup. The `4px` height is the progress-bar exception the
 * spacing scale states explicitly, not a value off the scale.
 *
 * **The digit boundary in miniature.** The fill is ASCII because CSS parses it;
 * the percentage beside it is Arabic-Indic because a human reads it. The track
 * is `aria-hidden` — colour is never the only signal, and the percentage column
 * carries the same figure in text.
 *
 * `tone="subscription"` takes the editorial indigo, which is the colour the
 * design system assigns to subscription and plan surfaces by name. It is not a
 * fifth identity colour and it is not a per-section palette.
 */
export function ShareBar({ ratio, tone = 'default' }: { ratio: number; tone?: 'default' | 'subscription' }) {
  const percent = Math.max(0, Math.min(100, Math.round(ratio * 100)));

  return (
    <span
      aria-hidden="true"
      className="block min-w-[4rem] overflow-hidden rounded-pill bg-surface-overlay [block-size:4px]"
    >
      <i
        style={{ '--progress': `${toMachineNumber(percent)}%` } as CSSProperties}
        className={cn(
          'block rounded-pill [block-size:100%] [inline-size:var(--progress,0%)]',
          tone === 'subscription' ? 'bg-editorial' : 'bg-accent',
        )}
      />
    </span>
  );
}
